/**
 * Contract V2 API Types
 * Based on OpenAPI spec for Contract V2 APIs
 */

/**
 * Free-form reference-data payload stored on a contract (`contracts.referenceData`).
 * It is the source of truth for reference data; `marketCap`, `volume` and
 * `latestSplitDt` are denormalized into typed columns on write, but `splitRatio`
 * has no typed column and can only be read from here.
 * `error` records the most recent failed fetch and is dropped on a successful write.
 */
export interface ContractReferenceData {
  marketCap?: number | string | null;
  volume?: number | string | null;
  latestSplitDt?: string | null;
  splitRatio?: string | null;
  source?: string | null;
  error?: unknown;
  [key: string]: unknown;
}

/**
 * Contract entity as returned by the V2 API
 */
export interface Contract {
  id: number;
  conId: number;
  symbol: string;
  secType: string;
  currency: string;
  exchange: string;
  primaryExchange: string;
  longName: string;
  validExchanges: string | null;
  timeZoneId: string | null;
  minTick: number | null;
  isin: string | null;
  minSize: number | null;
  sizeIncrement: number | null;
  shortable: boolean | null;
  eodhdTicker: string | null;
  eodhdName: string | null;
  eodhdType: string | null;
  eodhdExchange: string | null;
  eodhdExchangeName: string | null;
  eodhdOperatingMic: string | null;
  eodhdCountry: string | null;
  yahooTickerName: string | null;
  eodhdTicker1: string | null;
  eodhdTicker2: string | null;
  eodhdName1: string | null;
  eodhdName2: string | null;
  eodhdType1: string | null;
  eodhdType2: string | null;
  eodhdExchange1: string | null;
  eodhdExchange2: string | null;
  optionType: string | null;
  optionExchange: string | null;
  optionAllowed: boolean | null;
  tradableTimesUtc: string | null;
  verifiedAtUtc: string | null;
  figiComposite: string | null;
  priceSource: string | null;
  remarks: string | null;
  // Provider classification taxonomy, coarsest to finest. Ordinary editable contract
  // metadata, written through the contract upsert alongside symbol and longName.
  industry?: string | null;
  category?: string | null;
  subCategory?: string | null;
  // Reference data (read-only; populated by the stored reference-data job).
  // marketCap/volume arrive as strings because the API serializes Prisma Decimal.
  marketCap: number | string | null;
  // Market cap converted to USD (marketCap x FX rate from the contract's trading currency).
  // Read-only: recomputed by the stored reference-data PUT, never sent by a caller, so it can
  // move without marketCap moving. Null means never computed.
  marketCapUsd?: number | string | null;
  volume: number | string | null;
  // Average daily traded value in USD, derived from volume x price x FX-to-USD.
  // Read-only: recomputed by the stored reference-data PUT, never sent by a caller.
  avgDollarVolume?: number | string | null;
  // Borrow availability from the shortable refresh job (read-only; not accepted by upsert).
  // Serialized from Prisma Decimal, so they can arrive as strings, and are absent/null
  // until the job has run for the contract.
  shortableValue?: number | string | null;
  shortableShares?: number | string | null;
  latestSplitDt: string | null;
  // When reference data was last *written* -- stamped on failed fetches too, so it means
  // "last attempt", not "last good data". Drives the staleness window.
  referenceDataReceivedAtUtc?: string | null;
  referenceData?: ContractReferenceData | null;
  lastUsedAtUtc: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Input for creating or updating a contract
 */
export interface ContractUpsertInput {
  conId: number;
  symbol: string;
  secType: string;
  currency: string;
  exchange: string;
  primaryExchange: string;
  longName: string;
  validExchanges?: string | null;
  timeZoneId?: string | null;
  minTick?: number | null;
  isin?: string | null;
  minSize?: number | null;
  sizeIncrement?: number | null;
  shortable?: boolean | null;
  eodhdTicker?: string | null;
  eodhdName?: string | null;
  eodhdType?: string | null;
  eodhdExchange?: string | null;
  eodhdExchangeName?: string | null;
  eodhdOperatingMic?: string | null;
  eodhdCountry?: string | null;
  yahooTickerName?: string | null;
  eodhdTicker1?: string | null;
  eodhdTicker2?: string | null;
  eodhdName1?: string | null;
  eodhdName2?: string | null;
  eodhdType1?: string | null;
  eodhdType2?: string | null;
  eodhdExchange1?: string | null;
  eodhdExchange2?: string | null;
  optionType?: string | null;
  optionExchange?: string | null;
  optionAllowed?: boolean | null;
  tradableTimesUtc?: string | null;
  verifiedAtUtc?: string | null;
  figiComposite?: string | null;
  priceSource?: string | null;
  remarks?: string | null;
  industry?: string | null;
  category?: string | null;
  subCategory?: string | null;
}

/**
 * Stored reference data for a contract, as returned by
 * GET/PUT /v2/contracts/:conId/reference-data.
 */
export interface StoredContractReferenceData {
  referenceData: ContractReferenceData | null;
  referenceDataReceivedAtUtc: string | null;
}

/**
 * Paginated response for contracts list
 */
export interface PagedContractsResponse {
  currentPage: number;
  currentPageNumOfItems: number;
  totalPages: number;
  totalItems: number;
  items: Contract[];
}

/**
 * Parameters for finding contracts
 */
export interface FindContractsParams {
  optionsAllowed?: boolean;
  searchString?: string;
  searchColumn?: string;
  verifiedSinceMs?: number;
  orderByColumn?: string;
  orderByDirection?: "asc" | "desc";
  pageSize?: number;
  pageNumber?: number;
  structure?: "json" | "flat";
}

/**
 * Response for upsert contract operation
 */
export interface UpsertContractResponse {
  success: boolean;
  contract: Contract;
}

/**
 * Request for deleting contracts
 */
export interface DeleteContractsRequest {
  conIds: number[];
}

/**
 * Response for delete contracts operation
 */
export interface DeleteContractsResponse {
  success: boolean;
  deletedCount: number;
}

/**
 * Request for bulk upsert contracts
 */
export interface BulkUpsertRequest {
  contracts: ContractUpsertInput[];
}

/**
 * Response for bulk upsert operation
 */
export interface BulkUpsertResponse {
  success: boolean;
  upsertedCount: number;
}

/**
 * Upload structure for CSV files
 */
export interface UploadStructure {
  delimiter: string;
  headers: string[];
  headerCount: number;
}

/**
 * Summary of upload operation
 */
export interface UploadSummary {
  totalParsed: number;
  upsertedCount: number;
  parseErrors: number;
  failedContracts: FailedContract[];
}

export interface FailedContract {
  conId: number | null;
  error: string;
}

/**
 * Response for upload operation
 */
export interface UploadSummaryResponse {
  success: boolean;
  summary: UploadSummary;
}

/**
 * Request for verification operations
 */
export interface VerificationRequest {
  conIds: number[];
}

/**
 * Response for verification operations
 */
export interface UpdateVerificationsResponse {
  success: boolean;
  updatedCount: number;
}

/**
 * Parsed tradable time window
 */
export interface TradableTimeWindow {
  startTime: Date;
  endTime: Date;
}

/**
 * Split ratio has no typed column on `contracts` -- it lives only inside the
 * `referenceData` JSON -- so it is a synthetic table column: rendered from the JSON,
 * never sortable (the API can only order by real columns) and never inline-editable
 * (it is written through the reference-data PUT, not the contract upsert).
 */
export const SPLIT_RATIO_COLUMN_KEY = "splitRatio";

export type ContractColumnKey = keyof Contract | typeof SPLIT_RATIO_COLUMN_KEY;

/**
 * Column definition for contracts table
 */
export interface ContractColumnDef {
  key: ContractColumnKey;
  label: string;
  defaultVisible: boolean;
  sortable: boolean;
}

/**
 * Default column definitions for contracts table
 */
export const CONTRACT_COLUMNS: ContractColumnDef[] = [
  { key: "conId", label: "Con ID", defaultVisible: true, sortable: true },
  { key: "symbol", label: "Symbol", defaultVisible: true, sortable: true },
  { key: "longName", label: "Name", defaultVisible: true, sortable: true },
  { key: "secType", label: "Type", defaultVisible: true, sortable: true },
  { key: "exchange", label: "Exch", defaultVisible: true, sortable: true },
  { key: "primaryExchange", label: "Prim Exch", defaultVisible: true, sortable: true },
  { key: "remarks", label: "Remarks", defaultVisible: true, sortable: true },
  { key: "industry", label: "Industry", defaultVisible: true, sortable: true },
  { key: "category", label: "Category", defaultVisible: true, sortable: true },
  { key: "subCategory", label: "Sub Category", defaultVisible: true, sortable: true },
  { key: "isin", label: "ISIN", defaultVisible: false, sortable: true },
  { key: "figiComposite", label: "FIGI Composite", defaultVisible: true, sortable: true },
  { key: "timeZoneId", label: "Timezone", defaultVisible: false, sortable: true },
  { key: "minTick", label: "Min Tick", defaultVisible: false, sortable: true },
  { key: "minSize", label: "Min Size", defaultVisible: false, sortable: true },
  { key: "sizeIncrement", label: "Size Increment", defaultVisible: false, sortable: true },
  { key: "validExchanges", label: "Valid Exchanges", defaultVisible: false, sortable: false },
  { key: "optionAllowed", label: "Options", defaultVisible: true, sortable: true },
  { key: "optionExchange", label: "Option Exchange", defaultVisible: false, sortable: true },
  { key: "optionType", label: "Option Type", defaultVisible: false, sortable: true },
  { key: "yahooTickerName", label: "Yahoo Ticker", defaultVisible: false, sortable: true },
  { key: "eodhdTicker", label: "EODHD Ticker", defaultVisible: false, sortable: true },
  { key: "eodhdName", label: "EODHD Name", defaultVisible: false, sortable: false },
  { key: "eodhdType", label: "EODHD Type", defaultVisible: false, sortable: true },
  { key: "eodhdExchange", label: "EODHD Exchange", defaultVisible: false, sortable: true },
  { key: "eodhdCountry", label: "EODHD Country", defaultVisible: false, sortable: true },
  { key: "shortable", label: "Shortable", defaultVisible: false, sortable: true },
  { key: "shortableValue", label: "Shortable Value", defaultVisible: false, sortable: true },
  { key: "shortableShares", label: "Shortable Shares", defaultVisible: false, sortable: true },
  { key: "priceSource", label: "Price Source", defaultVisible: true, sortable: true },
  { key: "currency", label: "Curr", defaultVisible: true, sortable: true },
  { key: "marketCap", label: "Market Cap", defaultVisible: true, sortable: true },
  { key: "marketCapUsd", label: "Market Cap USD", defaultVisible: true, sortable: true },
  { key: "volume", label: "Volume", defaultVisible: true, sortable: true },
  { key: "avgDollarVolume", label: "Avg $ Volume", defaultVisible: true, sortable: true },
  { key: "latestSplitDt", label: "Split Date", defaultVisible: true, sortable: true },
  // Sourced from the referenceData JSON, so it cannot be sorted server-side.
  { key: SPLIT_RATIO_COLUMN_KEY, label: "Split Ratio", defaultVisible: true, sortable: false },
  { key: "verifiedAtUtc", label: "Verified", defaultVisible: true, sortable: true },
  { key: "createdAt", label: "Created", defaultVisible: false, sortable: true },
  { key: "updatedAt", label: "Updated", defaultVisible: false, sortable: true },
  { key: "lastUsedAtUtc", label: "Last Used", defaultVisible: true, sortable: true },
];

// ============================================
// Live Price API Types
// ============================================

/**
 * Available price data sources
 */
export type PriceSource = "IBKR" | "YAHOO" | "EODHD" | "BLOOMBERG";

/**
 * Price info from a single source
 */
export interface LivePriceInfo {
  source: PriceSource;
  isSourceMapped: boolean;
  price: number;
  error?: string;
}

/**
 * Response from live prices API
 * Map of conId (as string) to array of price info
 */
export type LivePricesResponse = Record<string, LivePriceInfo[]>;

/**
 * Calculate price drift percentage between two prices
 * Returns null if baseline price is 0 or invalid
 */
export function calculatePriceDrift(price: number, baselinePrice: number): number | null {
  if (baselinePrice <= 0 || price < 0) return null;
  return ((price - baselinePrice) / baselinePrice) * 100;
}

/**
 * Parse tradableTimesUtc string into array of time windows
 * Format: "startTime|endTime,startTime|endTime,..."
 */
export function parseTradableTimes(tradableTimesUtc: string | null): TradableTimeWindow[] {
  if (!tradableTimesUtc) return [];

  const windows: TradableTimeWindow[] = [];
  const pairs = tradableTimesUtc.split(",");

  for (const pair of pairs) {
    const [start, end] = pair.split("|");
    if (start && end) {
      windows.push({
        startTime: new Date(start.trim()),
        endTime: new Date(end.trim()),
      });
    }
  }

  return windows;
}
